import React, { useState } from 'react';
import './AddSubgroupModal.css';

const AddSubgroupModal = ({ parentGroup, availableGroups, onClose, onSubmit }) => {
  const [selectedSubgroup, setSelectedSubgroup] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedSubgroup) {
      setError('Please select a subgroup to add');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await onSubmit(parentGroup.cn, selectedSubgroup);
    } catch (error) {
      console.error('Failed to add subgroup:', error);
      setError('Failed to add subgroup. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubgroupChange = (e) => {
    setSelectedSubgroup(e.target.value);
    if (error) {
      setError('');
    }
  };

  // Filter out groups that are already subgroups of the parent
  const eligibleGroups = availableGroups.filter(group => {
    // Don't allow adding the group to itself
    if (group.cn === parentGroup.cn) {
      return false;
    }
    
    // Don't allow adding groups that are already subgroups
    if (parentGroup.memberof && parentGroup.memberof.includes(group.cn)) {
      return false;
    }
    
    return true;
  });

  return (
    <div className="modal-overlay">
      <div className="modal-content add-subgroup-modal">
        <div className="modal-header">
          <h3>Add Subgroup</h3>
          <button className="close-btn" onClick={onClose} disabled={isSubmitting}>
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="parent-group-info">
            <h4>Parent Group</h4>
            <div className="group-card">
              <div className="group-name">{parentGroup.cn}</div>
              {parentGroup.description && (
                <div className="group-description">{parentGroup.description}</div>
              )}
              <div className="group-stats">
                <span>Members: {parentGroup.member ? parentGroup.member.length : 0}</span>
                <span>Subgroups: {parentGroup.memberof ? parentGroup.memberof.length : 0}</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="add-subgroup-form">
            <div className="form-group">
              <label htmlFor="subgroup" className="form-label">
                Select Subgroup to Add *
              </label>
              
              {eligibleGroups.length === 0 ? (
                <div className="no-groups-available">
                  <p>No groups available to add as subgroups.</p>
                  <p className="help-text">
                    All existing groups are either already subgroups or cannot be added.
                  </p>
                </div>
              ) : (
                <select
                  id="subgroup"
                  value={selectedSubgroup}
                  onChange={handleSubgroupChange}
                  className={`form-select ${error ? 'error' : ''}`}
                  disabled={isSubmitting}
                >
                  <option value="">Choose a group...</option>
                  {eligibleGroups.map((group) => (
                    <option key={group.cn} value={group.cn}>
                      {group.cn}
                      {group.description && ` - ${group.description}`}
                    </option>
                  ))}
                </select>
              )}
              
              {error && (
                <span className="error-message">{error}</span>
              )}
            </div>

            {selectedSubgroup && (
              <div className="selected-subgroup-preview">
                <h4>Selected Subgroup</h4>
                {(() => {
                  const selectedGroup = eligibleGroups.find(g => g.cn === selectedSubgroup);
                  return selectedGroup ? (
                    <div className="group-card preview">
                      <div className="group-name">{selectedGroup.cn}</div>
                      {selectedGroup.description && (
                        <div className="group-description">{selectedGroup.description}</div>
                      )}
                      <div className="group-stats">
                        <span>Members: {selectedGroup.member ? selectedGroup.member.length : 0}</span>
                        <span>Current Parent Groups: {selectedGroup.memberof ? selectedGroup.memberof.length : 0}</span>
                      </div>
                    </div>
                  ) : null;
                })()}
              </div>
            )}

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting || !selectedSubgroup || eligibleGroups.length === 0}
              >
                {isSubmitting ? 'Adding...' : 'Add Subgroup'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddSubgroupModal;
